import React, { useEffect, useState } from "react";
import { X, Loader2, UploadCloud, FileText, CheckCircle2, Briefcase, MapPin, Clock, ShieldCheck, DollarSign } from "lucide-react";
import TextInputComp from "../../../components/customer/TextInputComp";
import TextAreaComp from "../../../components/customer/TextAreaComp";
import { Job } from "../../../types/Response/CustomerResponse";
import { useApplyJobMutation, useLazyGetJobDetailsQuery } from "../../../redux/actions/Customer/FooterCustomer";
import CommonButton from "../../../components/CommonButton";
import { useUploadCustomerFileMutation } from "../../../redux/actions/Customer/profileCustomer";

interface JobApplicationModalProps {
    job: Job;
    onClose: () => void;
}

const JobApplicationModal: React.FC<JobApplicationModalProps> = ({ job, onClose }) => {
    // API Hooks
    const [fetchJobDetails, { data: detailsResponse, isLoading: isLoadingDetails }] = useLazyGetJobDetailsQuery();
    const [applyJob, { isLoading: isSubmitting }] = useApplyJobMutation();
    const [uploadFile, { isLoading: isUploadingFile }] = useUploadCustomerFileMutation();

    const [isSuccess, setIsSuccess] = useState(false);
    const [resumeName, setResumeName] = useState("");

    const [form, setForm] = useState({
        fullName: "",
        email: "",
        phoneNumber: "",
        currentLocation: "",
        experienceYears: 0,
        currentCompany: "",
        currentCTC: "",
        expectedCTC: "",
        coverLetter: "",
        resumeUrl: "",
    });

    const isProcessing = isSubmitting || isUploadingFile || isLoadingDetails;
    
    // Unpack rich details data envelope (.value.data)
    const richJobDetails: Job = detailsResponse?.value?.data || job;

    // Fetch rich role specifics upon mounting wrapper
    useEffect(() => {
        if (job?.id) {
            fetchJobDetails(job.id);
        }
    }, [job, fetchJobDetails]);

    const handleResumeFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setResumeName(file.name);
        try {
            const response = await uploadFile({
                File: file,
                FolderName: "Careers/Resumes"
            }).unwrap();
            
            setForm((prev) => ({ ...prev, resumeUrl: response.fileUrl }));
        } catch (error) {
            console.error("Failed to upload document attachment:", error);
            alert("Upload failed. Please check your network connection.");
        }
    };

    const handleFormSubmission = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!form.resumeUrl) {
            alert("Please upload your resume before submitting.");
            return;
        }

        try {
            const payload = {
                ...form,
                careerJobId: job.id,
                experienceYears: Number(form.experienceYears),
            };

            await applyJob(payload).unwrap();
            setIsSuccess(true);
        } catch (error) {
            console.error("Submission pipeline transaction dropped:", error);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-end bg-slate-900/40 backdrop-blur-xs p-0 animate-fade-in text-left">
            <div className="absolute inset-0" onClick={onClose} />
            
            {/* Split Screen Sidebar Panel Container Drawer */}
            <div className="bg-[#FAFBF8] relative w-full max-w-2xl h-full shadow-2xl flex flex-col overflow-hidden z-10 animate-slide-left border-l border-slate-200">
                
                {/* Header Row */}
                <div className="p-5 border-b bg-white border-slate-100 flex items-center justify-between shadow-2xs">
                    <div>
                        <h2 className="text-base font-black text-slate-900 tracking-tight">{richJobDetails.jobTitle}</h2>
                        <p className="text-xs text-slate-400 font-semibold mt-0.5">{richJobDetails.department} Center • {richJobDetails.location}</p>
                    </div>
                    <button 
                        onClick={onClose}
                        disabled={isProcessing}
                        className="p-1.5 rounded-xl border border-slate-200 text-slate-400 hover:text-slate-700 bg-white cursor-pointer disabled:opacity-40"
                    >
                        <X size={16} />
                    </button>
                </div>

                {/* Main Scrollable Canvas Workspace */}
                <div className="flex-1 overflow-y-auto p-5 space-y-6">
                    
                    {/* --- VIEW SECTION: EXPLICIT DETAIL DESCRIPTIONS SPECIFICS --- */}
                    {isLoadingDetails ? (
                        <div className="py-10 text-center flex items-center justify-center gap-2 text-slate-400 font-bold text-xs italic">
                            <Loader2 className="w-4 h-4 animate-spin text-[#2F5E2E]" />
                            <span>Loading comprehensive role data...</span>
                        </div>
                    ) : (
                        <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4 shadow-3xs">
                            <div className="flex flex-wrap items-center gap-4 text-xs font-bold text-slate-500 border-b border-slate-50 pb-3">
                                <span className="flex items-center gap-1"><Clock size={14} className="text-[#2F5E2E]" /> {richJobDetails.employmentType}</span>
                                <span className="flex items-center gap-1"><ShieldCheck size={14} className="text-[#2F5E2E]" /> Experience Level: {richJobDetails.experienceLevel}</span>
                                {richJobDetails.salaryRange && (
                                    <span className="flex items-center gap-0.5"><DollarSign size={14} className="text-[#2F5E2E]" /> {richJobDetails.salaryRange}</span>
                                )}
                            </div>

                            <div className="space-y-1">
                                <h3 className="text-xs font-black uppercase text-slate-400 tracking-wider">Role Overview Description</h3>
                                <p className="text-xs text-slate-600 leading-relaxed font-medium whitespace-pre-line">{richJobDetails.description}</p>
                            </div>

                            {richJobDetails.requirements && (
                                <div className="space-y-1 pt-2">
                                    <h3 className="text-xs font-black uppercase text-slate-400 tracking-wider">Core Skill Capabilities & Requirements</h3>
                                    <p className="text-xs text-slate-600 leading-relaxed font-medium whitespace-pre-line">{richJobDetails.requirements}</p>
                                </div>
                            )}

                            {richJobDetails.responsibilities && (
                                <div className="space-y-1 pt-2">
                                    <h3 className="text-xs font-black uppercase text-slate-400 tracking-wider">Functional Day-To-Day Mandates</h3>
                                    <p className="text-xs text-slate-600 leading-relaxed font-medium whitespace-pre-line">{richJobDetails.responsibilities}</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* --- ACTION SECTION: INTERACTIVE SUBMISSION APPLICATION PANEL --- */}
                    <div className="border-t border-slate-200/80 pt-4">
                        {isSuccess ? (
                            <div className="bg-white border border-emerald-100 rounded-2xl p-8 text-center flex flex-col items-center justify-center space-y-3 shadow-3xs">
                                <CheckCircle2 size={32} className="text-[#2F5E2E] stroke-[2.5]" />
                                <h3 className="text-sm font-black text-slate-900 tracking-tight">Manuscript Submitted Successfully!</h3>
                                <p className="text-[11px] text-slate-400 font-medium max-w-xs leading-relaxed">
                                    Thank you for applying. Our talent parsing operations unit will evaluate your resume shortly.
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-5">
                                <div className="text-left">
                                    <h3 className="text-sm font-black text-slate-900 tracking-tight">Submit Portfolio Package</h3>
                                    <p className="text-[11px] text-slate-400 font-medium mt-0.5">Please provide authentic candidate identifiers parameters.</p>
                                </div>

                                <form onSubmit={handleFormSubmission} className="space-y-4 text-xs font-semibold text-slate-700">
                                    {isProcessing && (
                                        <div className="absolute inset-0 bg-white/70 backdrop-blur-3xs z-30 flex flex-col items-center justify-center gap-2">
                                            <Loader2 className="w-6 h-6 animate-spin text-[#2F5E2E]" />
                                            <span className="text-[11px] font-bold text-slate-600">Processing transactional operations rules...</span>
                                        </div>
                                    )}

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <TextInputComp
                                            showLabel label="Full Name" showStar 
                                            value={form.fullName} onChangeText={(text) => setForm({ ...form, fullName: text })}
                                        />
                                        <TextInputComp
                                            showLabel label="Email Address" showStar 
                                            value={form.email} onChangeText={(text) => setForm({ ...form, email: text })}
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <TextInputComp
                                            showLabel label="Phone Number" showStar 
                                            value={form.phoneNumber} onChangeText={(text) => setForm({ ...form, phoneNumber: text })}
                                        />
                                        <TextInputComp
                                            showLabel label="Current Location" showStar 
                                            value={form.currentLocation} onChangeText={(text) => setForm({ ...form, currentLocation: text })}
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                        <TextInputComp
                                            showLabel label="Experience (Years)" showStar type="number"
                                            value={String(form.experienceYears || "")} onChangeText={(text) => setForm({ ...form, experienceYears: Number(text) || 0 })}
                                        />
                                        <div className="sm:col-span-2">
                                            <TextInputComp
                                                showLabel label="Current Company Organization" 
                                                value={form.currentCompany} onChangeText={(text) => setForm({ ...form, currentCompany: text })}
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <TextInputComp
                                            showLabel label="Current CTC" 
                                            value={form.currentCTC} onChangeText={(text) => setForm({ ...form, currentCTC: text })}
                                        />
                                        <TextInputComp
                                            showLabel label="Expected CTC" 
                                            value={form.expectedCTC} onChangeText={(text) => setForm({ ...form, expectedCTC: text })}
                                        />
                                    </div>

                                    <TextAreaComp
                                        showLabel label="Cover Letter Remarks / Introduction"
                                        placeholder="Share your experience..."
                                        value={form.coverLetter} onChangeText={(text) => setForm({ ...form, coverLetter: text })}
                                    />

                                    {/* --- FILE UPLOAD COMPONENT MANIFEST --- */}
                                    <div className="space-y-1.5">
                                        <label className="text-xs font-bold text-slate-700 flex items-center gap-0.5">
                                            <span>Attach Resume Package File</span>
                                            <span className="text-rose-500">*</span>
                                        </label>
                                        <div className="relative border border-dashed border-slate-300 bg-white rounded-2xl p-4 text-center hover:border-[#6DBE45] flex flex-col items-center justify-center gap-2 overflow-hidden transition">
                                            <input 
                                                type="file" accept=".pdf,.doc,.docx" disabled={isProcessing}
                                                onChange={handleResumeFileSelected} className="absolute inset-0 opacity-0 cursor-pointer z-10"
                                            />
                                            {form.resumeUrl ? (
                                                <div className="flex items-center gap-2 text-[#2F5E2E] font-bold text-xs bg-emerald-50 border border-emerald-100 px-4 py-2 rounded-xl">
                                                    <FileText size={15} />
                                                    <span className="truncate max-w-[260px]">{resumeName || "Resume_Attachment.pdf"}</span>
                                                </div>
                                            ) : (
                                                <>
                                                    <UploadCloud size={22} className="text-slate-400" />
                                                    <p className="text-[11px] text-slate-500 font-medium">Select your resume file to upload</p>
                                                    <p className="text-[9px] text-slate-400">PDF, DOC, DOCX limits up to 5MB</p>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    {/* Footer Action Links */}
                                    <div className="flex justify-end gap-2.5 pt-4 border-t border-slate-100">
                                        <CommonButton 
                                            text="Cancel" variant="outline"  className="px-5 h-10 text-xs border-slate-200 bg-white text-slate-600"
                                            onClick={onClose} disabled={isProcessing}
                                        />
                                        <CommonButton 
                                            text="Submit Application" variant="filled" 
                                            disabled={isProcessing || !form.fullName.trim() || !form.email.trim()}
                                            className="bg-[#2F5E2E] hover:bg-[#1f3f1e] text-xs font-black px-5 h-10 shadow-sm"
                                        />
                                    </div>

                                </form>
                            </div>
                        )}
                    </div>

                </div>

            </div>
        </div>
    );
};

export default JobApplicationModal;